import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiEdit2, FiSave } from 'react-icons/fi';
import { PageHeader, RichTextEditor } from '../../components/common';
import { getNotice, updateNotice } from '../../services/api';
import { NOTICE_CATEGORIES } from './noticesConfig';
import styles from './EditNoticePage.module.css';

const EditNoticePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadNotice = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotice(id);
      setForm(res.data);
    } catch (err) {
      toast.error('Failed to load notice');
      navigate('/derasar/notices');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!id) return;
    loadNotice();
  }, [id, loadNotice]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!form.content.replace(/<[^>]+>/g, '').trim()) {
      toast.error('Content is required');
      return;
    }

    setSaving(true);
    try {
      await updateNotice(id, {
        title: form.title,
        content: form.content,
        category: form.category || null,
        expiryDate: form.expiryDate || null,
        isActive: form.isActive,
      });

      toast.success('Notice updated successfully');
      navigate(`/derasar/notices/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update notice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!form) return <div>Notice not found</div>;

  return (
    <div className={styles.page}>
      <PageHeader
        icon={<FiEdit2 />}
        title="Edit Notice"
        actions={(
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/derasar/notices/${id}`)}>
            <FiArrowLeft /> Back
          </button>
        )}
      />

      <form onSubmit={handleSubmit}>
        <div className={`card ${styles.card}`}>
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Notice Details</div>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                id="title"
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Notice title"
                maxLength="255"
                required
              />
            </div>

            <div className={styles.formRow}>
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={form.category || ''}
                  onChange={handleChange}
                >
                  <option value="">Select a category</option>
                  {NOTICE_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="expiryDate">Expiry Date</label>
                <input
                  id="expiryDate"
                  type="date"
                  name="expiryDate"
                  value={form.expiryDate || ''}
                  onChange={handleChange}
                />
                <small className={styles.helpText}>Leave blank for no expiry</small>
              </div>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <div className={styles.sectionLabel}>Content</div>

            <div className="form-group">
              <label>Content *</label>
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm((prev) => ({ ...prev, content: html }))}
                placeholder="Write your notice here..."
              />
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <div className={styles.sectionLabel}>Status</div>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
                className={styles.checkbox}
              />
              <span>Keep notice active</span>
            </label>
          </div>

          <div className={styles.divider} />

          <div className={styles.formActions}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => navigate(`/derasar/notices/${id}`)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <FiSave /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditNoticePage;
